import React, { useState } from 'react'
import { useQuery } from 'react-query'
import {
  Server,
  Activity,
  Cpu,
  HardDrive,
  Wifi,
  Database,
  Zap,
  AlertTriangle,
  CheckCircle,
  RefreshCw
} from 'lucide-react'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'
import { useWorkerStatusUpdates, useSystemHealthUpdates } from '../services/websocket'

export default function SystemPage() {
  const [activeTab, setActiveTab] = useState('overview')

  // Real-time updates
  useSystemHealthUpdates((data) => {
    console.log('System Health Update:', data)
  })

  useWorkerStatusUpdates((data) => {
    console.log('Worker Status Update:', data)
  })

  const { data: systemHealthData, isLoading: healthLoading } = useQuery(
    'system-health',
    () => adminApi.getSystemHealth(),
    {
      refetchInterval: 10000, // Refetch every 10 seconds
    }
  )

  const { data: workerStatusData, isLoading: workerLoading } = useQuery(
    'worker-status',
    () => adminApi.getWorkerStatus(),
    {
      refetchInterval: 15000, // Refetch every 15 seconds
    }
  )

  const systemHealth = systemHealthData?.data
  const workerStatus = workerStatusData?.data || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            System Monitoring
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Infrastructure health, worker status, and system performance
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button className="btn-secondary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh All
          </button>
        </div>
      </div>

      {/* System Health Overview */}
      {systemHealth && <SystemHealthOverview health={systemHealth} />}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('workers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'workers'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Workers
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'performance'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Performance
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab systemHealth={systemHealth} />}
      {activeTab === 'workers' && <WorkersTab workers={workerStatus} isLoading={workerLoading} />}
      {activeTab === 'performance' && <PerformanceTab />}
    </div>
  )
}

interface SystemHealthOverviewProps {
  health: any
}

function SystemHealthOverview({ health }: SystemHealthOverviewProps) {
  const services = [
    { name: 'API Server', status: health.api_status, icon: Server },
    { name: 'Database', status: health.database_status, icon: Database },
    { name: 'Redis Cache', status: health.redis_status, icon: Zap },
    { name: 'File Storage', status: 'connected', icon: HardDrive },
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {services.map((service) => (
        <div key={service.name} className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <service.icon className="h-8 w-8 text-gray-400" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-900">{service.name}</h3>
                <div className="flex items-center mt-1">
                  {service.status === 'healthy' || service.status === 'connected' ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className={`text-sm capitalize ${
                    service.status === 'healthy' || service.status === 'connected'
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {service.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function OverviewTab({ systemHealth }: { systemHealth: any }) {
  if (!systemHealth) {
    return <div className="animate-pulse">Loading system health...</div>
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Generation Queue</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Pending</span>
              <span className="font-medium">{systemHealth.generation_metrics?.pending || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Processing</span>
              <span className="font-medium">{systemHealth.generation_metrics?.processing || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Completed Today</span>
              <span className="font-medium">{systemHealth.generation_metrics?.completed_today || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Failed Today</span>
              <span className="font-medium">{systemHealth.generation_metrics?.failed_today || 0}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Worker Status</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Active Workers</span>
              <span className="font-medium">
                {systemHealth.celery_workers?.active || 0}/{systemHealth.celery_workers?.total || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Queue Count</span>
              <span className="font-medium">{systemHealth.celery_workers?.queues?.length || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function WorkersTab({ workers, isLoading }: { workers: any[], isLoading: boolean }) {
  if (isLoading) {
    return <div className="animate-pulse">Loading worker status...</div>
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium">Celery Workers</h3>
      </div>
      <div className="card-body p-0">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Worker ID</th>
                <th>Status</th>
                <th>Active Tasks</th>
                <th>Processed</th>
                <th>Failed</th>
                <th>Load Average</th>
                <th>Memory Usage</th>
                <th>Last Heartbeat</th>
              </tr>
            </thead>
            <tbody>
              {workers.map((worker) => (
                <tr key={worker.id}>
                  <td className="font-mono text-sm">{worker.hostname}</td>
                  <td>
                    <span className={`badge ${
                      worker.status === 'online' ? 'badge-success' : 'badge-danger'
                    }`}>
                      {worker.status}
                    </span>
                  </td>
                  <td>{worker.active_tasks}</td>
                  <td>{worker.processed_tasks}</td>
                  <td>{worker.failed_tasks}</td>
                  <td>{worker.load_average?.[0]?.toFixed(2) || 'N/A'}</td>
                  <td>{worker.memory_usage ? `${worker.memory_usage}%` : 'N/A'}</td>
                  <td>{new Date(worker.last_heartbeat).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {workers.length === 0 && (
          <div className="text-center py-12">
            <Activity className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No workers found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No Celery workers are currently active.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function PerformanceTab() {
  // Mock performance data
  const performanceMetrics = [
    { name: 'CPU Usage', value: '45%', status: 'good' },
    { name: 'Memory Usage', value: '67%', status: 'warning' },
    { name: 'Disk Usage', value: '23%', status: 'good' },
    { name: 'Network I/O', value: '12 MB/s', status: 'good' },
  ]

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {performanceMetrics.map((metric) => (
          <div key={metric.name} className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{metric.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                </div>
                <div className={`h-3 w-3 rounded-full ${
                  metric.status === 'good' ? 'bg-green-500' :
                  metric.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                }`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">System Resources</h3>
        </div>
        <div className="card-body">
          <p className="text-gray-500">Performance charts and detailed metrics will be implemented here.</p>
        </div>
      </div>
    </div>
  )
}